// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { listApprovals, submitReviewDecision } from '../services/hitlService';
import { fetchPlanFile } from '../services/planService';
import { ApiError } from '../services/api';
import type { HitlApproval, HitlApprovalState, HitlReviewSummary, PlanFile } from '../types';
import { HITL_APPROVAL_TYPE } from '../utils/constants';
import { useActiveSite } from './useActiveSite';

/**
 * HITL is a proxy over a Temporal workflow: the decision POST acknowledges the
 * *signal* it sent, not the state transition the workflow will perform. A
 * single read-after-write therefore often still reports PENDING. Re-read on
 * this backoff (~3s total) before giving up and trusting our own decision.
 */
const DECISION_SETTLE_BACKOFF_MS = [500, 1_000, 1_500] as const;

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * A decision HITL accepted but has not yet reflected in the approval record.
 * Overlaid on the plan so the reviewer isn't told their approval didn't land;
 * dropped the moment the server reports any non-PENDING state of its own.
 */
interface PinnedDecision {
  approvalId: string;
  status: 'APPROVED' | 'REJECTED';
  comment?: string;
  decidedAt: string;
}

/**
 * A HITL approval, adapted to the shape the plan detail tabs consume.
 * `storeKey` is the approval's `attributes.objectKey` — the Object Store
 * key the actual plan narration file was uploaded under.
 */
export interface ApprovalPlan {
  approvalId: string;
  reviewId: string;
  storeKey: string;
  dc_site_id: string;
  date: string;
  status: HitlApprovalState;
  review_date?: string;
  reviewComment?: string;
  reviews: HitlReviewSummary[];
  attributes: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/**
 * The date the plan is *for*, which is what the detail header shows. Day-ahead
 * plans carry an `executionStart` timestamp and are typically created the day
 * before they run, so falling back to `createdAt` would label them a day early.
 */
function resolvePlanDate(approval: HitlApproval, attributes: Record<string, unknown>): string {
  const executionStart = attributes.executionStart;
  if (typeof executionStart === 'string' && executionStart !== '') {
    const parsed = new Date(executionStart);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  }
  return (approval.createdAt ?? '').slice(0, 10);
}

function toApprovalPlan(approval: HitlApproval, siteLabel: string): ApprovalPlan {
  const attributes = approval.attributes ?? {};
  const objectKey = attributes.objectKey;
  const review = approval.reviews[0];
  const reviewComment = review?.attributes?.comment;
  return {
    approvalId: approval.approvalId,
    reviewId: review?.reviewId ?? '',
    storeKey: typeof objectKey === 'string' ? objectKey : '',
    dc_site_id: siteLabel,
    date: resolvePlanDate(approval, attributes),
    status: approval.state,
    review_date: approval.state !== 'PENDING' ? (approval.updatedAt ?? undefined) : undefined,
    reviewComment:
      approval.state !== 'PENDING' && typeof reviewComment === 'string' ? reviewComment : undefined,
    reviews: approval.reviews,
    attributes,
    createdAt: approval.createdAt ?? '',
    updatedAt: approval.updatedAt ?? '',
  };
}

/**
 * Overlay a decision HITL hasn't caught up to yet.
 *
 * `status` alone isn't enough: `toApprovalPlan` gates `review_date` and
 * `reviewComment` on the approval's state, and PlanAuditTrail titles each timeline
 * entry from `review.state` rather than `plan.status` — so patching only the
 * top-level status renders "Review Pending Review" directly above "Plan
 * Approved". The whole record is moved to where the server is about to put it.
 */
function applyPinnedDecision(plan: ApprovalPlan, pin: PinnedDecision): ApprovalPlan {
  return {
    ...plan,
    status: pin.status,
    review_date: pin.decidedAt,
    reviewComment: pin.comment,
    // TODO: revisit if HITL ever grows quorum rules — one reviewer's decision
    // would no longer settle the approval.
    reviews: plan.reviews.map(review =>
      review.reviewId === plan.reviewId
        ? {
            ...review,
            state: pin.status,
            attributes: {
              ...(review.attributes ?? {}),
              ...(pin.comment ? { comment: pin.comment } : {}),
            },
          }
        : review
    ),
  };
}

interface UsePlanDetailResult {
  plan: ApprovalPlan | null;
  planFile: PlanFile | null;
  loading: boolean;
  error: string | null;
  updateStatus: (_status: 'APPROVED' | 'REJECTED', _comment?: string) => Promise<void>;
}

export function usePlanDetail(approvalId: string | undefined): UsePlanDetailResult {
  const { siteId, siteLabel } = useActiveSite();
  const [plan, setPlan] = useState<ApprovalPlan | null>(null);
  const [planFile, setPlanFile] = useState<PlanFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pinned, setPinned] = useState<PinnedDecision | null>(null);
  const activeRef = useRef(true);
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);

  const fetchApproval = useCallback(async (): Promise<HitlApproval | undefined> => {
    const approvals = await listApprovals({
      siteId,
      approvalType: HITL_APPROVAL_TYPE,
    });
    return approvals.find(a => a.approvalId === approvalId);
  }, [approvalId, siteId]);

  /** Full load: spinner, error state, and the narration download. Mount only. */
  const load = useCallback(async () => {
    if (!approvalId) return;
    try {
      setLoading(true);
      setError(null);
      setPinned(null);

      const approval = await fetchApproval();
      if (!activeRef.current) return;
      if (!approval) {
        setPlan(null);
        setPlanFile(null);
        setError('Approval not found');
        return;
      }

      const approvalPlan = toApprovalPlan(approval, siteLabel);
      setPlan(approvalPlan);
      const file = approvalPlan.storeKey
        ? await fetchPlanFile(approval.siteId, approvalPlan.storeKey)
        : null;
      if (!activeRef.current) return;
      setPlanFile(file);
    } catch (err) {
      if (!activeRef.current) return;
      setError((err as Error).message ?? 'Failed to load plan details');
    } finally {
      if (activeRef.current) setLoading(false);
    }
  }, [approvalId, fetchApproval, siteLabel]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Re-read just the approval record after a decision. Deliberately silent: no
   * `loading` (the page must not flash its spinner behind the modal) and no
   * narration re-download (a decision can't change the plan file).
   *
   * A missing approval or a failed read is a no-op returning null, not an
   * error — surfacing it would replace the just-approved plan with the
   * "Couldn't load this plan" empty state over a transient blip.
   */
  const refreshApproval = useCallback(async (): Promise<HitlApprovalState | null> => {
    try {
      const approval = await fetchApproval();
      if (!activeRef.current || !approval) return null;
      setPlan(toApprovalPlan(approval, siteLabel));
      return approval.state;
    } catch {
      return null;
    }
  }, [fetchApproval, siteLabel]);

  /** Re-read until HITL reports a decision, or the backoff runs out. */
  const settle = useCallback(async (): Promise<HitlApprovalState | null> => {
    let state = await refreshApproval();
    for (const delay of DECISION_SETTLE_BACKOFF_MS) {
      if (state !== 'PENDING' || !activeRef.current) break;
      await sleep(delay);
      if (!activeRef.current) break;
      state = await refreshApproval();
    }
    return state;
  }, [refreshApproval]);

  const updateStatus = useCallback(
    async (status: 'APPROVED' | 'REJECTED', comment?: string) => {
      const target = plan;
      if (!target?.reviewId) {
        setError('This approval has no review to act on');
        return;
      }

      try {
        await submitReviewDecision(target.reviewId, {
          decision: status,
          ...(comment ? { comment } : {}),
        });
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 409)) throw err;
        await settle();
        return;
      }

      const settled = await settle();
      if (!activeRef.current) return;

      setPinned(
        settled === 'PENDING' || settled === null
          ? { approvalId: target.approvalId, status, comment, decidedAt: new Date().toISOString() }
          : null
      );
    },
    [plan, settle]
  );

  const effectivePlan = useMemo(
    () =>
      plan && pinned && pinned.approvalId === plan.approvalId && plan.status === 'PENDING'
        ? applyPinnedDecision(plan, pinned)
        : plan,
    [plan, pinned]
  );

  return { plan: effectivePlan, planFile, loading, error, updateStatus };
}
